import { isTypeContentPageHeader, TypePage } from '@platform/contentful';
import ContentfulSeoData from './ContentfulSeoData';
import ContentfulAccordionSection from './ContentfulAccordionSection';
import ContentfulCardsSection from './ContentfulCardsSection';
import ContentfulCtaSection from './ContentfulCtaSection';
import ContentfulEmbedSection from './ContentfulEmbedSection';
import ContentfulFeaturedProjectsSection from './ContentfulFeaturedProjectsSection';
import ContentfulHeroCardSection from './ContentfulHeroCardSection';
import ContentfulHeroQuoteSection from './ContentfulHeroQuoteSection';
import ContentfulLogoSection from './ContentfulLogoSection';
import ContentfulLogoWithQuoteSection from './ContentfulLogoWithQuoteSection';
import ContentfulNavigationCardsSection from './ContentfulNavigationCardsSection';
import ContentfulPersonsSection from './ContentfulPersonsSection';
import ContentfulProcessStepsSection from './ContentfulProcessStepsSection';
import ContentfulProgramComparisonSection from './ContentfulProgramComparisonSection';
import ContentfulProgramsSection from './ContentfulProgramsSection';
import ContentfulSimpleColumnSection from './ContentfulSimpleColumnSection';
import ContentfulTestimonialSection from './ContentfulTestimonialSection';
import ContentfulTimelineSection from './ContentfulTimelineSection';
import ContentfulVideoSection from './ContentfulVideoSection';
import ContentfulWysiwygSection from './ContentfulWysiwygSection';
import ContentfulContentPageHeader from './ContentfulContentPageHeader';
import ContentfulContentNavigation from './ContentfulContentNavigation';
import ContentfulEmbedSectionRaw from './ContentfulEmbedSectionRaw';
import ContentfulEmbedZohoForm from './ContentfulEmbedZohoForm';
import ContentfulLandingPageHeader from './ContentfulLandingPageHeader';
import ContentfulProgramsPageHeader from './ContentfulProgramsPageHeader';
import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import TableOfContentsLeftNav from './util/TableOfContentsLeftNav';
import cx from 'classix';

const ContentfulPage = ({
  pageEntry,
}: {
  pageEntry: TypePage<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>;
}) => {
  const pageData = useContentfulLiveUpdates(pageEntry);
  // This is extremely weird; some pages have a left-side nav panel, that is enabled by "some" random section using the
  // TypeTableOfContentsSetting value. It seems to always be in the first section, and always a ContentPageHeader? For now,
  // spot-check for this and if enabled turn on the side panel.
  let isLeftNavEnabled = false;
  const headerSection = pageData.fields?.sections?.[0] ?? null;
  if (headerSection && isTypeContentPageHeader(headerSection)) {
    isLeftNavEnabled = headerSection.fields.tableOfContents;
  }

  const tocHeadersList: string[] = [];
  let tocHeadersTitleText = '';
  const tocHeadersRecord: Record<string, string> = {};
  // Assume the first section is the header section and extract the title text
  if (headerSection && isTypeContentPageHeader(headerSection)) {
    tocHeadersTitleText = headerSection.fields.header ?? '';
  }
  /* eslint-disable @typescript-eslint/no-explicit-any */
  // Skip the first (header) section
  pageData.fields.sections?.slice(1)?.forEach((section) => {
    if ('header' in (section?.fields as any)) {
      tocHeadersRecord[section?.sys.id as string] = (section?.fields as any)?.header;
      tocHeadersList.push((section?.fields as any)?.header);
    }
    /* eslint-enable @typescript-eslint/no-explicit-any */
  });

  return (
    // TODO: Skeptical this max width is appropriate for all pages
    // TODO: It seems like we use Open Sans as the default font on these pages. Should we keep that?
    <div
      className={cx(
        'relative block bg-white px-2 font-open ',
        isLeftNavEnabled ? 'sm:grid sm:grid-cols-[240px_auto]' : 'sm:block',
        // Offset the default md:pt-12 in app.tsx
        'md:-mt-12',
      )}
    >
      {isLeftNavEnabled && (
        <TableOfContentsLeftNav title={tocHeadersTitleText} tocHeaders={tocHeadersList} />
      )}
      <div className='flex flex-col sm:ml-2.5 sm:mr-2.5'>
        {pageData.fields.seo && <ContentfulSeoData entry={pageData.fields.seo} />}
        {pageData.fields.sections &&
          pageData.fields.sections.map((section) => (
            <div
              id={tocHeadersRecord[section?.sys.id as string]?.replaceAll(' ', '_') ?? undefined}
              // className='contents'
              data-contentful-model={section?.sys.contentType.sys.id}
              key={section?.sys.id}
            >
              <ContentfulAccordionSection entry={section} />
              <ContentfulCardsSection entry={section} />
              <ContentfulContentPageHeader entry={section} tocItems={tocHeadersList} />
              <ContentfulContentNavigation entry={section} />
              <ContentfulCtaSection entry={section} />
              <ContentfulEmbedSection entry={section} />
              <ContentfulEmbedSectionRaw entry={section} />
              <ContentfulEmbedZohoForm entry={section} />
              <ContentfulFeaturedProjectsSection entry={section} />
              <ContentfulHeroCardSection entry={section} />
              <ContentfulHeroQuoteSection entry={section} />
              <ContentfulLandingPageHeader entry={section} />
              <ContentfulLogoSection entry={section} />
              <ContentfulLogoWithQuoteSection entry={section} />
              <ContentfulNavigationCardsSection entry={section} />
              <ContentfulPersonsSection entry={section} />
              <ContentfulProcessStepsSection entry={section} />
              <ContentfulProgramComparisonSection entry={section} />
              <ContentfulProgramsPageHeader entry={section} />
              <ContentfulProgramsSection entry={section} />
              <ContentfulSimpleColumnSection entry={section} />
              <ContentfulTestimonialSection entry={section} />
              <ContentfulTimelineSection entry={section} />
              <ContentfulVideoSection entry={section} />
              <ContentfulWysiwygSection entry={section} />
            </div>
          ))}
      </div>
    </div>
  );
};

export default ContentfulPage;
